// The worker avatar: a faceless worker in a hard hat and coveralls that people style to look like themselves. See
// WorkerAvatar.vue for the artwork and WorkerAvatarCustomizer.vue for the "Suit up" panel.
import { colorDistance, colorMix } from '../color-picker/color-picker';
import type { WorkerAvatarLook } from '../../cross-app-shared/types/worker-avatar';

export type WorkerAvatarColorKey = Exclude<keyof WorkerAvatarLook, 'hairLength'>;

export const workerAvatarColorKeys: WorkerAvatarColorKey[] = ['skin', 'hair', 'hat', 'suit', 'stripe'];

export const WORKER_AVATAR_LOOK_DEFAULT: WorkerAvatarLook = {
  hairLength: 'short',
  skin: '#D69E7C',
  hair: '#6B4430',
  hat: '#FFC400',
  suit: '#FFFFFF',
  stripe: '#0FA96A',
};

export interface WorkerAvatarColorOption {
  color: string;
  label: string;
}

export const workerAvatarColorOptions: Record<WorkerAvatarColorKey, WorkerAvatarColorOption[]> = {
  skin: [
    { color: '#F6D3B8', label: 'Skin tone 1, lightest' },
    { color: '#E8B48E', label: 'Skin tone 2' },
    { color: '#D69E7C', label: 'Skin tone 3' },
    { color: '#B97A55', label: 'Skin tone 4' },
    { color: '#8D5A3B', label: 'Skin tone 5' },
    { color: '#5E3A28', label: 'Skin tone 6, deepest' },
  ],
  hair: [
    { color: '#1E1B1A', label: 'Black' },
    { color: '#4A2F22', label: 'Dark brown' },
    { color: '#6B4430', label: 'Brown' },
    { color: '#8E3F22', label: 'Auburn' },
    { color: '#D9A54A', label: 'Blonde' },
    { color: '#A9ADB1', label: 'Grey' },
  ],
  hat: [
    { color: '#FFC400', label: 'Yellow' },
    { color: '#FFFFFF', label: 'White' },
    { color: '#FF5D1D', label: 'Orange' },
    { color: '#0FA96A', label: 'Green' },
    { color: '#92A5B9', label: 'Grey' },
  ],
  suit: [
    { color: '#FFFFFF', label: 'White' },
    { color: '#92A5B9', label: 'Grey' },
    { color: '#FFC400', label: 'Yellow' },
    { color: '#FF5D1D', label: 'Orange' },
  ],
  stripe: [
    { color: '#0FA96A', label: 'Green' },
    { color: '#FFC400', label: 'Yellow' },
    { color: '#FF5D1D', label: 'Orange' },
    { color: '#081848', label: 'Navy' },
    { color: '#92A5B9', label: 'Grey' },
  ],
};

// A company whose colours the worker can wear: offered first in the hat, suit and stripe rows, and as a one-click
// "Wear <name> colours" for the hat and stripes.
export interface WorkerAvatarCompany {
  name: string;
  colors: WorkerAvatarColorOption[];
  wear: { hat: string; stripe: string };
}

// A row of swatches. The company's own colours, if any, come first, set apart from Toolkit's by a divider. The row ends
// with a custom swatch that opens a colour picker, showing the chosen colour if it isn't one of the others.
export type WorkerAvatarSwatch =
  | ({ kind: 'color' } & WorkerAvatarColorOption)
  | { kind: 'divider' }
  | { kind: 'custom'; color: string | null };

export function workerAvatarSwatches(
  key: WorkerAvatarColorKey,
  company: WorkerAvatarCompany | null,
  look: WorkerAvatarLook,
): WorkerAvatarSwatch[] {
  const standard = workerAvatarColorOptions[key];
  const own =
    company && key !== 'skin' && key !== 'hair'
      ? company.colors.filter(
          (c) =>
            !standard.some((s) => s.color === c.color) &&
            // Stripes the colour of the suit would vanish.
            !(key === 'stripe' && c.color === look.suit),
        )
      : [];
  const listed = [...own, ...standard];
  return [
    ...own.map((c) => ({ kind: 'color' as const, ...c })),
    ...(own.length ? [{ kind: 'divider' as const }] : []),
    ...standard.map((c) => ({ kind: 'color' as const, ...c })),
    { kind: 'custom', color: listed.some((c) => c.color === look[key]) ? null : look[key] },
  ];
}

export function workerAvatarWearsCompany(look: WorkerAvatarLook, company: WorkerAvatarCompany): boolean {
  return look.hat === company.wear.hat && look.stripe === company.wear.stripe;
}

// Every fill the artwork needs, including the shades derived from the chosen colours.
export interface WorkerAvatarColors {
  skin: string;
  hair: string;
  // The shadow the face casts on long hair.
  hairShade: string;
  hat: string;
  hatHighlight: string;
  suit: string;
  // Pockets and collar.
  suitShade: string;
  stripe: string;
  // Behind the face in the round portrait: Toolkit green, unless the hat is green too, so the hat never disappears.
  faceBackground: string;
}

const HAT_HIGHLIGHTS: Record<string, string> = {
  '#FFC400': '#FFD959',
  '#FFFFFF': '#E2E7E8',
  '#FF5D1D': '#FF8B5E',
  '#0FA96A': '#4CC28F',
  '#92A5B9': '#B7C4D1',
};
const TOOLKIT_GREEN = '#0FA96A';

export function workerAvatarColors(look: WorkerAvatarLook): WorkerAvatarColors {
  return {
    skin: look.skin,
    hair: look.hair,
    hairShade: colorMix(look.hair, '#000000', 0.22),
    hat: look.hat,
    hatHighlight: HAT_HIGHLIGHTS[look.hat] ?? colorMix(look.hat, '#FFFFFF', 0.3),
    suit: look.suit,
    suitShade: look.suit === '#FFFFFF' ? '#E2E7E8' : colorMix(look.suit, '#000000', 0.12),
    stripe: look.stripe,
    faceBackground: colorDistance(look.hat, TOOLKIT_GREEN) < 90 ? '#163241' : TOOLKIT_GREEN,
  };
}
